import { ChevronRight } from 'lucide-react';
import { useEffect, useState } from 'react';
import { useNavigate } from 'react-router-dom';
import { api } from '../../api';
import { useAuth } from '../../auth-context';
import { formatTashkentDate, t } from '../../i18n';
import type { FeedItem, ParentOverview, PrayerStatus, PrayerTime } from '../../types';
import { AppShell } from '../shared/AppShell';
import { Avatar } from '../shared/Avatar';
import { Card } from '../shared/Card';
import { FeedRow, useFeedPlayer } from './FeedRow';

const PRAYER_NAME_KEY: Record<PrayerTime, string> = {
  bomdod: 'prayer.bomdod',
  peshin: 'prayer.peshin',
  asr: 'prayer.asr',
  shom: 'prayer.shom',
  xufton: 'prayer.xufton',
};

export function ParentDashboard() {
  const { logout } = useAuth();
  const navigate = useNavigate();
  const [overview, setOverview] = useState<ParentOverview | null>(null);
  const [feed, setFeed] = useState<FeedItem[]>([]);
  const player = useFeedPlayer(feed, setFeed);

  useEffect(() => {
    api.parentOverview().then(setOverview);
    api.parentFeed({ limit: 10 }).then((r) => setFeed(r.items));
  }, []);

  return (
    <AppShell>
      <main className="mx-auto flex min-h-dvh max-w-2xl flex-col gap-6 p-5 pb-12">
        <header className="flex items-start justify-between gap-3">
          <div>
            <div className="text-[11px] font-medium uppercase tracking-wide text-ink-subtle">
              {t('parent.panel')}
            </div>
            {overview ? (
              <h1 className="mt-1 text-[22px] font-medium">
                {t('parent.today', { date: formatTashkentDate(overview.quran_day) })}
              </h1>
            ) : (
              <h1 className="mt-1 h-7 w-40 animate-pulse rounded bg-surface-muted" />
            )}
          </div>
          <div className="flex gap-3 text-[13px] text-ink-muted">
            <button onClick={() => navigate('/parent/week')}>Hafta</button>
            <button onClick={() => navigate('/parent/settings')}>{t('common.settings')}</button>
            <button
              onClick={async () => {
                await logout();
                navigate('/', { replace: true });
              }}
            >
              {t('common.logout')}
            </button>
          </div>
        </header>

        <section className="grid grid-cols-2 gap-3 sm:grid-cols-4">
          <Metric label={t('parent.metrics.today')} value={overview?.totals.recitations_today} />
          <Metric label={t('parent.metrics.all')} value={overview?.totals.recitations_all_time} />
          <Metric
            label={t('parent.metrics.unheard')}
            value={overview?.totals.unheard_count}
            tone={overview && overview.totals.unheard_count > 0 ? 'info' : 'neutral'}
          />
          <Metric
            label={t('parent.metrics.streak')}
            value={overview?.totals.longest_current_streak}
            suffix="kun"
          />
        </section>

        <section className="grid grid-cols-1 gap-3 sm:grid-cols-3">
          {overview
            ? overview.children.map((c) => (
                <button
                  key={c.id}
                  onClick={() => navigate(`/parent/children/${c.id}`)}
                  className="text-left active:scale-[0.99]"
                >
                  <Card>
                    <div className="flex items-start gap-3">
                      <div
                        className={`ramp-${c.color_ramp}`}
                        onClick={(e) => e.stopPropagation()}
                      >
                        <Avatar
                          initial={c.avatar_initial}
                          ramp={c.color_ramp}
                          size="md"
                          src={c.avatar_url ?? undefined}
                        />
                      </div>
                      <div className="flex flex-1 flex-col">
                        <span className="text-[16px] font-medium">{c.name}</span>
                        <span className="text-[12px] text-ink-muted">{c.age} yosh</span>
                      </div>
                    </div>
                    <div className="mt-3 text-[12px] text-ink-muted">
                      {c.current_surah.number}. {c.current_surah.name_uz}
                    </div>
                    <div className={`ramp-${c.color_ramp} mt-3`}>
                      <PrayerBars prayers={c.today.prayers} />
                    </div>
                    <div className="mt-3 text-[12px] text-ink-muted">
                      {c.today.total_recitations} ta yozuv · {c.streak_days} kun silsila
                    </div>
                    {c.memorization ? (
                      <div className="mt-1 text-[12px] text-ink-muted">
                        {t('module.memorization')} · {c.memorization.total_count}/{c.memorization.total_target}
                      </div>
                    ) : null}
                  </Card>
                </button>
              ))
            : Array.from({ length: 3 }).map((_, i) => (
                <Card key={i}>
                  <div className="h-[110px] animate-pulse rounded bg-surface-muted" />
                </Card>
              ))}
        </section>

        <section className="flex flex-col gap-3">
          <div className="flex items-center justify-between">
            <span className="text-[11px] font-medium uppercase tracking-wide text-ink-subtle">
              {t('parent.feed.title')}
            </span>
            <button
              onClick={() => navigate('/parent/feed')}
              className="flex items-center gap-1 text-[13px] text-ink-muted"
            >
              {t('parent.feed.show_all')}
              <ChevronRight size={14} />
            </button>
          </div>
          {feed.length === 0 ? (
            <p className="text-[13px] text-ink-muted">{t('parent.feed.empty')}</p>
          ) : (
            <ul className="flex flex-col gap-2">
              {feed.map((item) => (
                <FeedRow key={item.key} item={item} player={player} />
              ))}
            </ul>
          )}
        </section>
      </main>
    </AppShell>
  );
}

function Metric({
  label,
  value,
  suffix,
  tone = 'neutral',
}: {
  label: string;
  value: number | undefined;
  suffix?: string;
  tone?: 'neutral' | 'info';
}) {
  const valueClass = tone === 'info' ? 'text-info' : 'text-ink';
  return (
    <Card>
      <div className="text-[11px] font-medium uppercase tracking-wide text-ink-subtle">{label}</div>
      <div className={`mt-1 text-[22px] font-medium tabular-nums ${valueClass}`}>
        {value === undefined ? '—' : value}
        {value !== undefined && suffix ? <span className="ml-1 text-[12px] text-ink-muted">{suffix}</span> : null}
      </div>
    </Card>
  );
}

function PrayerBars({ prayers }: { prayers: Array<{ prayer_time: PrayerTime; status: PrayerStatus; recitation_count: number }> }) {
  return (
    <div className="flex gap-1">
      {prayers.map((p) => {
        let bg = 'bg-ink/10';
        if (p.status === 'done') bg = 'bg-child-accent';
        else if (p.status === 'in_progress') bg = 'bg-child-fill ring-1 ring-child-accent/50';
        else if (p.status === 'skipped') bg = 'bg-ink/10 ring-1 ring-dashed ring-ink/20';
        return (
          <span
            key={p.prayer_time}
            title={t(PRAYER_NAME_KEY[p.prayer_time])}
            className={`h-3 flex-1 rounded-sm ${bg}`}
          />
        );
      })}
    </div>
  );
}
